import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, CardBody, CardTitle, Button, ButtonGroup } from 'reactstrap';
import { useMutation, useQueryClient } from 'react-query';
import { useRole } from 'hooks/useRole';
import { useAuth } from 'hooks/useAuth';
import moment from 'moment';
import Apaexlinecolumn from '../../AllCharts/apex/apaexlinecolumn';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import MiniWidget from './mini-widget';
import { getGroupStatusCountsApi } from 'api/report';
import { setGroupSummary } from 'store/actions';
import { sumArray, getSearchColumns } from 'utils/tools';
import MainFilter from 'components/Common/MainFilter';

// redux
import { useSelector, useDispatch } from 'react-redux';

// Members Chart
const series1 = [
  { name: 'Members', data: [12, 14, 2, 47, 42, 15, 47, 75, 65, 19, 14] },
];
const options1 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#50a5f1'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

// Dependants Chart
const series2 = [
  { name: 'Dependants', data: [25, 66, 41, 89, 63, 25, 44, 12, 36, 9, 54] },
];
const options2 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#00b3e4'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

// Defaulted Chart
const series3 = [
  { name: 'Defaulted', data: [35, 53, 93, 47, 54, 24, 47, 75, 65, 19, 14] },
];
const options3 = {
  chart: { sparkline: { enabled: !0 } },
  stroke: { curve: 'smooth', width: 2 },
  colors: ['#FF5252'],
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      inverseColors: false,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [25, 100, 100, 100],
    },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    marker: { show: false },
  },
};

const GroupReports = () => {
  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isFinance,
    isDirector,
    addresses,
  } = useRole();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { groupSummary } = useSelector((state) => ({
    groupSummary: state?.reports?.group,
  }));

  const [timePeriod, setTimePeriod] = useState('year');

  const reports = [
    {
      title: 'Active Groups',
      icon: 'mdi mdi-account',
      color: 'info',
      value:
        groupSummary?.data?.length
          ? (
              groupSummary.data.find((s) => s.monthName === moment().format('MMM'))?.totalActiveGroups || 0
            ).toLocaleString()
          : 0,
      desc: 'Active Groups',
      series: series1,
      options: options1,
      arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
    },
    {
      title: 'Total Groups',
      icon: 'mdi mdi-account-multiple',
      color: 'primary',
      value:
        groupSummary?.data?.length
          ? (
              groupSummary.data.find((s) => s.monthName === moment().format('MMM'))?.totalGroups || 0
            ).toLocaleString()
          : 0,
      desc: 'Total Groups',
      series: series2,
      options: options2,
      arrowUpDown: 'mdi mdi-arrow-down ms-1 text-danger',
    },
    {
      title: 'Transfers',
      icon: 'mdi mdi-transfer',
      color: 'warning',
      value:
        groupSummary?.data?.length
          ? (
              groupSummary.data.find((s) => s.monthName === moment().format('MMM'))?.totalGroupTransfers || 0
            ).toLocaleString()
          : 0,
      desc: 'Group Transfers',
      series: series3,
      options: options3,
      arrowUpDown: 'mdi mdi-arrow-up ms-1 text-success',
    },
  ];

  const { mutate: mutateGroupCounts, data } = useMutation(
    (payload) => getGroupStatusCountsApi(payload),
    {
      onSuccess: (res) => {
        dispatch(setGroupSummary(res));
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const fetchDataForPeriod = (period) => {
    let fromDate, toDate;
    if (period === 'month') {
      fromDate = moment().startOf('month').format('DD-MM-YYYY');
      toDate = moment().format('DD-MM-YYYY');
    } else if (period === 'year') {
      fromDate = moment().startOf('year').format('DD-MM-YYYY');
      toDate = moment().format('DD-MM-YYYY');
    }
    mutateGroupCounts({
      fromDate,
      toDate,
      address: {},
    });
  };

  useEffect(() => {
    fetchDataForPeriod(timePeriod);
  }, [timePeriod]);

  const columnChartSeries = useMemo(() => {
    if (timePeriod === 'year') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonthIndex = moment().month();
      const yearData = months.slice(0, currentMonthIndex + 1).map((month) => {
        const summary = groupSummary?.data?.find((item) => item.monthName === month) || {};
        return {
          totalGroups: summary.totalGroups || 0,
          totalActiveGroups: summary.totalActiveGroups || 0,
        };
      });
      return [
        { name: 'Total Groups', data: yearData.map((d) => d.totalGroups) },
        { name: 'Active Groups', data: yearData.map((d) => d.totalActiveGroups) },
      ];
    } else if (timePeriod === 'month') {
      const currentMonthData = groupSummary?.data?.filter((item) => item.monthName === moment().format('MMM')) || [];
      return [
        { name: 'Total Groups', data: currentMonthData.map((item) => item.totalGroups || 0) },
        { name: 'Active Groups', data: currentMonthData.map((item) => item.totalActiveGroups || 0) },
      ];
    }
    return [
      { name: 'Total Groups', data: [] },
      { name: 'Active Groups', data: [] },
    ];
  }, [groupSummary, timePeriod]);

  const categories = useMemo(() => {
    if (timePeriod === 'year') {
      return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].slice(
        0,
        moment().month() + 1
      );
    } else if (timePeriod === 'month') {
      return [moment().format('MMM')];
    }
    return [];
  }, [timePeriod]);

  const onSearch = (data) => {
    const addressData = {};
    if (isFacilitator && addresses?.wardId) {
      addressData['wardId'] = addresses?.wardId;
    }
    if (isSiteCord && data?.wardId) {
      addressData['wardId'] = data?.wardId.value;
    }
    if (data?.wardId) {
      addressData['wardId'] = data?.wardId.value;
    }
    if (data?.constituencyId) {
      addressData['constituencyId'] = data?.constituencyId.value;
    }
    if (data?.countyId) {
      addressData['countyId'] = data?.countyId.value;
    }
    if (data?.regionId) {
      addressData['regionId'] = data?.regionId.value;
    }

    const fromDate = data?.startDate?.value || (timePeriod === 'year' ? moment().startOf('year').format('DD-MM-YYYY') : moment().startOf('month').format('DD-MM-YYYY')); // Updated
    const toDate = data?.endDate?.value || moment().format('DD-MM-YYYY');

    mutateGroupCounts({
      fromDate,
      toDate,
      wardIds: getSearchColumns()?.find((t) => t.name === 'wardIds')?.value,
      customDate: !!(data?.startDate?.value || data?.endDate?.value),
      address: addressData,
    });
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Reports" breadcrumbItem="Group Reports" />
        <MainFilter onSearch={onSearch} showDateFilter />
        <Row>
          <MiniWidget reports={reports} />
        </Row>
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-4">Group Activity</CardTitle>
                <ButtonGroup className="mb-3">
                  <Button
                    color={timePeriod === 'month' ? 'primary' : 'secondary'}
                    onClick={() => setTimePeriod('month')}
                  >
                    Month
                  </Button>
                  <Button
                    color={timePeriod === 'year' ? 'primary' : 'secondary'}
                    onClick={() => setTimePeriod('year')}
                  >
                    Year
                  </Button>
                </ButtonGroup>
                <Apaexlinecolumn
                  dataColors='["--bs-info", "--bs-success"]'
                  series={columnChartSeries}
                  categories={categories}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default GroupReports;